import { RecurringReportConfig, ReportConfig, ReportMenuOption, ReportType } from './types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { downloadFile, httpGet, httpPost, httpPut } from '../utils';
import jstz from 'jstimezonedetect';
import { usePricingModulesState } from '../../context/contexts/PricingModuleContext';
import { ListItemProps } from '@silverfort/sf-components/dist/ListItem';
import { REPORT_MODULES_DATA } from '@silverfort-nodelibs/common/utils/consts';

interface ReportsDrawerContextProps {
    selectedReportOption: ReportMenuOption;
    setSelectedReportOption: (type: ReportMenuOption) => void;
    getRecurringReportData: (type: ReportType) => Promise<RecurringReportConfig[]>;
    upsertRecurringReportData: (config: RecurringReportConfig) => Promise<RecurringReportConfig | null>;
    generateReport: (
        report: ReportConfig
    ) => Promise<{ fileName: string; fileGenerated: boolean; date: string } | null>;
    downloadReport: (data: { fileName: string; date: string }) => boolean;
    availableModules: ListItemProps<string>[];
    rulesOptions: { title: string; value: string }[];
    riskDataStartDate: string;
}

const DEFAULT_CONTEXT: ReportsDrawerContextProps = {
    selectedReportOption: ReportMenuOption.SingleRiskReport,
    setSelectedReportOption: () => {},
    getRecurringReportData: () => Promise.resolve([]),
    upsertRecurringReportData: () => Promise.resolve(null),
    generateReport: () => Promise.resolve(null),
    downloadReport: () => false,
    availableModules: [],
    rulesOptions: [],
    riskDataStartDate: ''
};

export const ReportDrawerContext = createContext<ReportsDrawerContextProps>(DEFAULT_CONTEXT);

export const ReportsDrawerContextProvider = ({ children }) => {
    const [selectedReportOption, setSelectedReportOption] = useState<ReportMenuOption>(
        ReportMenuOption.SingleRiskReport
    );
    const [availableModules, setAvailableModules] = useState<ListItemProps<string>[]>([]);
    const [rulesOptions, setRulesOptions] = useState<{ title: string; value: string }[]>([]);
    const [riskDataStartDate, setRiskDataStartDate] = useState<string>('');

    const { pricingModulesState } = usePricingModulesState();

    useEffect(() => {
        const getRulesOptions = async () => {
            try {
                const response: { data: { id: number; name: string }[] | null } = await httpGet('/reports/rules');
                const namesToIds: { [key: string]: number[] } = {};
                for (const rule of response.data ?? []) {
                    if (namesToIds[rule.name]) {
                        namesToIds[rule.name].push(rule.id);
                    } else {
                        namesToIds[rule.name] = [rule.id];
                    }
                }
                const options = Object.entries(namesToIds).map(([name, ids]) => ({
                    title: name,
                    value: ids.toString()
                }));
                setRulesOptions(options);
            } catch (error) {
                console.error('Error fetching rules options:', error);
            }
        };

        const getRiskDataStartDate = async () => {
            try {
                const response: { data: string | null } = await httpGet('/reports/riskDataStartDate');
                if (!response.data) {
                    console.error('Error fetching riskDataStartDate');
                } else {
                    setRiskDataStartDate(response.data);
                }
            } catch (error) {
                console.error('Error fetching riskDataStartDate', error);
            }
        };
        getRulesOptions();
        getRiskDataStartDate();
    }, []);

    useEffect(() => {
        const moduleOptions: ListItemProps<string>[] = Object.entries(REPORT_MODULES_DATA).map(([key, value]) => {
            const disabled =
                !!value.licenseModules.length &&
                // todo: when cloud env risk report is enabled, make this check dependant on the env. for now, on prem only
                !value.licenseModules.some((module) => pricingModulesState?.[module].onPremEnabled);
            return {
                value: key,
                title: value.title,
                disabled,
                tooltipDescription: disabled ? 'This module isn’t available in your current license' : ''
            };
        });
        setAvailableModules(moduleOptions);
    }, [pricingModulesState]);

    const getRecurringReportData = async (type: ReportType) => {
        try {
            const response: { data: RecurringReportConfig[] | null } = await httpGet(`/reports/configs/${type}`);
            return response.data ?? [];
        } catch (error) {
            console.error('Error fetching recurring report data:', error);
            return [];
        }
    };

    const upsertRecurringReportData = async (config: RecurringReportConfig) => {
        try {
            if (config.id) {
                return (
                    (await httpPut(`/reports/configs/${config.id}`, config)) as { data: RecurringReportConfig | null }
                ).data;
            }
            return ((await httpPost('/reports/configs', config)) as { data: RecurringReportConfig | null }).data;
        } catch (error) {
            console.error('Error fetching recurring report data:', error);
            return null;
        }
    };

    const generateReport = async (report: ReportConfig) => {
        const localTimeZone = encodeURIComponent(jstz.determine().name());
        const { metadata, type } = report;

        try {
            const response: { data: { fileName: string; fileGenerated: boolean; date: string } | null } =
                await httpPost(`/reports`, {
                    metadata,
                    type,
                    localTimeZone
                });
            return response.data;
        } catch (error) {
            console.error('Error generating report:', error);
            return null;
        }
    };

    const downloadReport = (data: { fileName: string; date: string }) => {
        if (!data) return false;
        try {
            downloadFile(`/reports/download/${data.fileName}/${data.date}`, data.fileName);
            return true;
        } catch (error) {
            console.error('Error downloading report:', error);
            return false;
        }
    };

    const value = useMemo(
        () => ({
            selectedReportOption,
            setSelectedReportOption,
            getRecurringReportData,
            upsertRecurringReportData,
            generateReport,
            downloadReport,
            availableModules,
            rulesOptions,
            riskDataStartDate
        }),
        [selectedReportOption, availableModules, rulesOptions, riskDataStartDate]
    );

    return <ReportDrawerContext.Provider value={value}>{children}</ReportDrawerContext.Provider>;
};

export const useReportsDrawer = (): ReportsDrawerContextProps => useContext(ReportDrawerContext);
